<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>

    <main-layout>

        <div v-if="!loading" id="sl-user-management" class="sl-page">

            <!-- Page Header -->
            <div class="sl-page__header">
                <div class="sl-page__title">
                    {{ $t('user_management.page_title') }}
                </div>

                <!-- Create user button -->
                <button class="lsn-btn lsn-btn--primary ml-auto" @click="createEntityUser">
                    {{ $t('user_management.add_user_btn.label') }}
                </button>
            </div>

            <!-- Page Content -->
            <div class="sl-page__content text-gray-500 relative">

                <template v-if="entityActiveUsers.length">

                    <p class="mb-2">
                        {{ $t('user_management.active_user_table.label') }}
                    </p>

                    <!-- Active users table (if any) -->
                    <table class="w-full">
                        <thead>
                            <tr>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.name') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.username') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.language') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.email') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.role') }}
                                </th>
                                <th v-can="'user:edit'" class="py-1 max-w-[30px]">
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="user in entityActiveUsers" :key="user.uid" :class="{'bg-gray-100 text-gray-400': !user.is_active}">
                                <td class="py-1 px-2 text-sm border border-gray-300">
                                    {{ user.last_name }}&nbsp;{{ user.first_name }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300 font-mono">
                                    {{ user.username }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300 font-mono text-center">
                                    {{ user.configurations?.language }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300">
                                    {{ user.email }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t(`role.${user.role}.label`) }}
                                </td>
                                <td v-can="'user:edit'" class="py-1 text-sm border border-gray-300 cursor-pointer hover:bg-primary-100 max-w-[20px]" @click="editEntityUser(user.id)">
                                    <mdi-icon class="w-[18px] mx-auto text-primary-300" icon="mdiPencil" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>

                <!-- No user -->
                <div v-else>
                    {{ $t('user_management.no_linked_user') }}
                </div>

                <!-- Inactive users table (if any) -->
                <template v-if="entityInactiveUsers.length && isStaff">
                    <p class="mt-8 mb-2">
                        {{ $t('user_management.inactive_user_table.label') }}
                    </p>
                    <table class="w-full">
                        <thead>
                            <tr>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.name') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.username') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.language') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.email') }}
                                </th>
                                <th class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t('user_management.table_header.role') }}
                                </th>
                                <th v-can="'user:edit'" class="py-1 max-w-[20px]">
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="user in entityInactiveUsers" :key="user.uid" :class="{'bg-gray-100 text-gray-400': !user.is_active}">
                                <td class="py-1 px-2 text-sm border border-gray-300">
                                    {{ user.last_name }}&nbsp;{{ user.first_name }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300 font-mono">
                                    {{ user.username }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300 font-mono text-center">
                                    {{ user.configurations?.language }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300">
                                    {{ user.email }}
                                </td>
                                <td class="py-1 px-2 text-sm border border-gray-300">
                                    {{ $t(`role.${user.role}.label`) }}
                                </td>
                                <td v-can="'user:edit'" class="py-1 max-w-[20px] border border-gray-300 cursor-pointer hover:bg-primary-100" @click="editEntityUser(user.id)">
                                    <mdi-icon class="w-[18px] mx-auto text-primary-300 text-center" icon="mdiPencil" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </template>

            </div>

            <!-- user form -->
            <router-view />

        </div>

        <!-- page loading -->
        <div v-else class="sl-page w-4/5 mt-10 text-center">
            <img class="inline" src="/img/loader-5.gif">
        </div>

    </main-layout>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

import MainLayout from '@/layouts/MainLayout.vue';
import { orderBy }    from 'lodash-es';

export default
{
    name: 'UserManagement',

    components:
    {
        MainLayout,
    },

    data()
    {
        return {
            loading: false,
        };
    },

    computed:
    {
        entityActiveUsers()
        {
            const entityUsers = this.$store.state.users.entityUsers.filter(user => user.is_active);

            return orderBy(entityUsers, ['last_name', 'username'], ['asc', 'asc']);
        },

        entityInactiveUsers()
        {
            const entityUsers = this.$store.state.users.entityUsers.filter(user => !user.is_active);

            return orderBy(entityUsers, ['last_name', 'username'], ['asc', 'asc']);
        },

        isStaff()
        {
            return this.$store.getters['account/isStaff'];
        },
    },

    created()
    {
        this.loading = true;

        this.$store.dispatch('users/fetchEntityUsers').then(() =>
        {
            this.loading = false;
        });
    },

    methods:
    {
        createEntityUser()
        {
            this.$router.push({
                name:   'user-new',
                params: this.$route.params,
            });
        },

        editEntityUser(userId)
        {
            const params = this.$route.params;
            params.user_id = userId;

            this.$router.push({
                name:   'user-edit',
                params: params,
            });
        },
    },
};

</script>

<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style lang="scss" scoped>



</style>
